import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import '../styles/design-system.css';

export default function MatchesPage() {
  const [matches, setMatches] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for demonstration - in real app this would come from database
  const mockMatches = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      age: 28,
      location: 'London, UK',
      photo: '/default-avatar.png',
      personalityType: 'Social Butterfly',
      personalityScore: 85,
      compatibilityScore: 92,
      interests: ['Culture', 'Food', 'Nightlife', 'Art', 'Music'],
      travelStyle: 'Social & Cultural',
      bio: 'Love exploring new cultures through food and local experiences. Always up for trying new restaurants and meeting locals. Passionate about street art and live music.',
      isOnline: true,
      lastActive: '2 minutes ago'
    },
    {
      id: 2,
      name: 'Mike Rodriguez',
      age: 31,
      location: 'Manchester, UK',
      photo: '/default-avatar.png',
      personalityType: 'Adventure Seeker',
      personalityScore: 92,
      compatibilityScore: 88,
      interests: ['Adventure', 'Outdoors', 'Sports', 'Hiking', 'Climbing'],
      travelStyle: 'Active & Adventurous',
      bio: 'Passionate about outdoor adventures and extreme sports. Looking for travel buddies who love hiking, climbing, and adrenaline activities. Always seeking new challenges.',
      isOnline: false,
      lastActive: '1 hour ago'
    },
    {
      id: 3,
      name: 'Emma Lawrence',
      age: 26,
      location: 'Birmingham, UK',
      photo: '/default-avatar.png',
      personalityType: 'Cultural Explorer',
      personalityScore: 78,
      compatibilityScore: 85,
      interests: ['History', 'Art', 'Museums', 'Architecture', 'Literature'],
      travelStyle: 'Cultural & Educational',
      bio: 'Art history graduate who loves exploring museums and historical sites. Always researching the cultural background of places I visit. Fascinated by ancient architecture.',
      isOnline: true,
      lastActive: '5 minutes ago'
    },
    {
      id: 4,
      name: 'David Kim',
      age: 29,
      location: 'Liverpool, UK',
      photo: '/default-avatar.png',
      personalityType: 'Social Butterfly',
      personalityScore: 88,
      compatibilityScore: 90,
      interests: ['Music', 'Nightlife', 'Social', 'Festivals', 'Photography'],
      travelStyle: 'Social & Entertainment',
      bio: 'Music lover and social butterfly. Love discovering local music scenes and meeting people from different backgrounds. Always up for festivals and live events.',
      isOnline: false,
      lastActive: '3 hours ago'
    },
    {
      id: 5,
      name: 'Lisa Patel',
      age: 27,
      location: 'Leeds, UK',
      photo: '/default-avatar.png',
      personalityType: 'Adventure Seeker',
      personalityScore: 85,
      compatibilityScore: 87,
      interests: ['Hiking', 'Photography', 'Nature', 'Wildlife', 'Landscapes'],
      travelStyle: 'Nature & Photography',
      bio: 'Photography enthusiast who loves capturing beautiful landscapes and wildlife. Always looking for off-the-beaten-path destinations and unique natural wonders.',
      isOnline: true,
      lastActive: '10 minutes ago'
    }
  ];

  useEffect(() => {
    loadUserProfile();
    setMatches(mockMatches);
    setLoading(false);
  }, []);

  const loadUserProfile = async () => {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes(user);
      setUserProfile({
        id: user.userId,
        name: attributes.name || 'User',
        personalityScore: attributes.personalityScore || 0,
        personalityType: attributes.personalityType || 'Unknown'
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleConnect = (matchId) => {
    // In real app, this would send a connection request
    alert(`Connection request sent to ${matches.find(m => m.id === matchId)?.name}!`);
  };

  const handleMessage = (matchId) => {
    // In real app, this would open messaging
    alert(`Opening chat with ${matches.find(m => m.id === matchId)?.name}!`);
  };

  const filteredMatches = matches.filter(match => {
    const matchesFilter = filter === 'all' || 
      (filter === 'online' && match.isOnline) ||
      (filter === 'high-compatibility' && match.compatibilityScore >= 85);
    
    const matchesSearch = match.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      match.interests.some(interest => interest.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const renderMatchCard = (match) => (
    <div key={match.id} style={{ 
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        padding: '24px'
      }}>
        {/* Header with photo and basic info */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img 
              src={match.photo} 
              alt={match.name}
              style={{ 
                width: '100px', 
                height: '100px', 
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid #f3f4f6'
              }}
            />
            {match.isOnline && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                width: '20px',
                height: '20px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }} />
            )}
          </div>
          
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start', 
              marginBottom: '12px',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  {match.name}
                </h3>
                <p style={{ 
                  margin: '0 0 8px 0', 
                  color: '#6b7280', 
                  fontSize: '16px',
                  fontWeight: '500'
                }}>
                  {match.age} years old • {match.location}
                </p>
                <p style={{ 
                  margin: '0', 
                  color: '#9ca3af', 
                  fontSize: '14px'
                }}>
                  {match.travelStyle}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  backgroundColor: match.compatibilityScore >= 85 ? '#10b981' : 
                                match.compatibilityScore >= 70 ? '#f59e0b' : '#ef4444',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {match.compatibilityScore}% Match
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <span style={{ 
                backgroundColor: '#e5e7eb', 
                padding: '6px 12px', 
                borderRadius: '16px', 
                fontSize: '14px',
                fontWeight: '500',
                marginRight: '12px',
                color: '#374151'
              }}>
                {match.personalityType}
              </span>
              <span style={{ 
                color: match.isOnline ? '#10b981' : '#6b7280', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {match.isOnline ? '🟢 Online' : `Last active ${match.lastActive}`}
              </span>
            </div>
          </div>
        </div>
        
        {/* Bio */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ 
            margin: '0', 
            fontSize: '16px', 
            lineHeight: '1.6',
            color: '#374151'
          }}>
            {match.bio}
          </p>
        </div>
        
        {/* Interests */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#374151'
          }}>
            Interests & Activities:
          </h4>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px'
          }}>
            {match.interests.map(interest => (
              <span key={interest} style={{
                backgroundColor: '#f3f4f6',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                border: '1px solid #e5e7eb'
              }}>
                {interest}
              </span>
            ))}
          </div>
        </div>
        
        {/* Action buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={() => handleConnect(match.id)}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '120px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
          >
            Connect
          </button>
          <button 
            onClick={() => handleMessage(match.id)}
            style={{
              backgroundColor: 'white',
              color: '#2563eb',
              border: '2px solid #2563eb',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '120px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#2563eb';
            }}
          >
            Message
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '80px 20px',
        backgroundColor: '#f9fafb',
        minHeight: '100vh'
      }}>
        <div style={{ 
          fontSize: '20px', 
          color: '#6b7280',
          fontWeight: '500'
        }}>
          Loading your travel matches...
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f9fafb'
    }}>
      {/* Header Section */}
      <section style={{ 
        backgroundColor: 'white', 
        padding: '60px 20px', 
        borderBottom: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)', 
            margin: '0 0 16px 0', 
            color: '#1f2937',
            fontWeight: '700',
            lineHeight: '1.2'
          }}>
            Your Travel Matches
          </h1>
          <p style={{ 
            fontSize: 'clamp(1rem, 3vw, 1.25rem)', 
            color: '#6b7280', 
            margin: '0',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.5'
          }}>
            Connect with compatible travel companions based on your personality and interests
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ 
        padding: '40px 20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Filters and Search */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '40px',
          flexWrap: 'wrap',
          gap: '20px',
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={() => setFilter('all')}
              style={{
                backgroundColor: filter === 'all' ? '#2563eb' : 'white',
                color: filter === 'all' ? 'white' : '#374151',
                border: '2px solid #d1d5db',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              All Matches
            </button>
            <button 
              onClick={() => setFilter('online')}
              style={{
                backgroundColor: filter === 'online' ? '#2563eb' : 'white',
                color: filter === 'online' ? 'white' : '#374151',
                border: '2px solid #d1d5db',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Online Now
            </button>
            <button 
              onClick={() => setFilter('high-compatibility')}
              style={{
                backgroundColor: filter === 'high-compatibility' ? '#2563eb' : 'white',
                color: filter === 'high-compatibility' ? 'white' : '#374151',
                border: '2px solid #d1d5db',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              High Compatibility
            </button>
          </div>
          
          <input
            type="text"
            placeholder="Search by name or interests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              width: 'clamp(200px, 30vw, 300px)',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        {/* Results */}
        <div>
          <div style={{ 
            marginBottom: '30px',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ 
              margin: '0', 
              color: '#374151',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {filteredMatches.length} {filteredMatches.length === 1 ? 'Match' : 'Matches'} Found
            </h3>
          </div>
          
          {filteredMatches.length > 0 ? (
            <div>
              {filteredMatches.map(renderMatchCard)}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '80px 20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ 
                color: '#6b7280', 
                marginBottom: '16px',
                fontSize: '24px',
                fontWeight: '600'
              }}>
                No matches found
              </h3>
              <p style={{ 
                color: '#9ca3af',
                fontSize: '16px',
                maxWidth: '400px',
                margin: '0 auto',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters or search terms to find more travel companions.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
} 